import { useEffect, useState } from "react";
import {
  Input,
  InputNumber,
  Loader,
  Message,
  SelectPicker,
  Uploader,
  useToaster,
} from "rsuite";
import {
  Table,
  Column as RSuiteColumn,
  ColumnGroup,
  HeaderCell,
  Cell,
} from "rsuite-table";
import { UploadQuoteLabels as LABELS } from "../UploadQuoteConstants";
import CustomModal from "../../../components/custom-modal";
import {
  IAdditionalInputPayload,
  IQuoteUploadFiltersResponse,
} from "../../../services/interfaces/IQuoteUpload";
import {
  convertDateToMonthYear,
  getCurrentMonthYear,
  getMondayOfCurrentWeekLabel,
} from "../../../utils/helpers/mapModulesToIcon";
import { formatNumberwithCommas } from "../../../utils/formatter";
import { FileType, UploaderProps } from "rsuite/esm/Uploader";
import FileUploadIcon from "@rsuite/icons/FileUpload";
import Button from "../../../components/button";
import {
  useIndexUploadSubmit,
  useIndexValue,
  useProjectedReleaseUploadSubmit,
} from "../../../react-query/hooks/HQuoteUpload";
import moment from "moment";
import UserGuideDownloadIcon from "../../../assets/user-guide-download-white.svg";
import { useAppSelector } from "../../../services/redux/hooks/hooks";
import { Accordion } from "rsuite";
import { FaPlus, FaMinus } from "react-icons/fa";
import useResponsiveWidth from "../../../react-query/hooks/useResponsiveWidth";

type Props = {
  data?: [];
  filters: IQuoteUploadFiltersResponse;
  commodity: string | undefined;
  screenStatus: boolean;
};

type DistrictLbsRow = {
  district: string;
  lbs: number | string;
};

type DistrictQuotesRow = {
  district: string;
  base_sku?: string;
  quotes: number | string;
};

const AdditionalInputs = ({
  data,
  filters,
  commodity,
  screenStatus,
}: Props) => {
  const width = useResponsiveWidth();
  const toaster = useToaster();
  const PlusIcon = FaPlus as React.FC;
  const MinusIcon = FaMinus as React.FC;
  const [activeKeyValue, setActivekeyValue] = useState<string>("");
  const [openModal, setOPenModal] = useState<boolean>(false);
  const [filesList, setFilesList] = useState<FileType[]>([]);
  const [validationMessage, setValidationMessage] = useState<string>("");
  const [isAdditionalInputsUploading, setAdditionalInputsUploading] =
    useState(false);

  const [monthYear, setMonthYear] = useState<string | null>(null);
  const [closevalue, setCloseValue] = useState<number | null>(null);
  const [additionalInputsPayload, setAdditionalInputsPayload] =
    useState<IAdditionalInputPayload>({
      file: undefined,
      date: undefined,
      projectedReleaseDate: undefined,
      commodity: commodity ?? undefined,
      email: localStorage.getItem("userEmail"),
      uploadType: undefined,
    });
  const [uploadStatus, setUploadStatus] = useState<{
    uploadProjectedRelease: boolean;
    uploadAgentPricing: boolean;
  }>({
    uploadProjectedRelease: false,
    uploadAgentPricing: false,
  });

  const userDetails = useAppSelector((state) => {
    return state.user;
  });

  const {
    mutate,
    uploadSuccess,
    setUploadSuccess,
    errorMessage,
    setErrorMessage,
  } = useProjectedReleaseUploadSubmit();

  const {
    mutate: indexMutate,
    isSuccess,
    isPending,
    isRefetch,
    setIsRefetch,
  } = useIndexUploadSubmit();

  const { indexTableData, isIndexStatusLoading, refetch } = useIndexValue(
    commodity,
    localStorage.getItem("userEmail"),
  );

  useEffect(() => {
    if (commodity) {
      setAdditionalInputsPayload({
        ...additionalInputsPayload,
        commodity: commodity,
      });
      setMonthYear(null);
    }
  }, [commodity]);

  useEffect(() => {
    if (uploadSuccess && openModal) {
      setAdditionalInputsUploading(false);
      setUploadSuccess(false);
      setOPenModal(false);
      setFilesList([]);
      setAdditionalInputsPayload({
        file: undefined,
        date: undefined,
        commodity: commodity ?? undefined,
        email: localStorage.getItem("userEmail"),
        uploadType: undefined,
      });
      setValidationMessage("");
      toaster.push(
        <Message showIcon type={"success"} closable>
          <strong>
            {uploadStatus.uploadAgentPricing
              ? "Agent Pricing "
              : "Projected Release "}
            Upload Successful!
          </strong>
        </Message>,
        { placement: "topCenter", duration: 5000 },
      );
    }
    setUploadStatus({
      uploadProjectedRelease: false,
      uploadAgentPricing: false,
    });
  }, [uploadSuccess]);
  useEffect(() => {
    if (errorMessage && openModal) {
      setAdditionalInputsUploading(false);
      toaster.push(
        <Message showIcon type={"error"} closable>
          {Array.isArray(errorMessage) ? (
            errorMessage.map((error) => (
              <div>
                <strong>{error}</strong>
              </div>
            ))
          ) : (
            <strong>{errorMessage}</strong>
          )}
        </Message>,
        { placement: "topCenter", duration: 5000 },
      );
      setErrorMessage(undefined);
    }
  }, [errorMessage]);

  const handleToggle = (key: string) => {
    if (activeKeyValue === key) {
      setActivekeyValue("");
    } else {
      setActivekeyValue(key);
    }
  };

  const handleClickUpload = (
    obj: { [key: string]: boolean },
    type: "uploadProjectedRelease" | "uploadAgentPricing",
  ) => {
    setOPenModal(true);
    setAdditionalInputsPayload({
      ...additionalInputsPayload,
      uploadType: type,
    });
    setUploadStatus({ ...uploadStatus, ...obj });
  };

  const handleFieldChange = (
    value: string | Date | null,
    key: keyof IAdditionalInputPayload,
  ) => {
    setAdditionalInputsPayload({
      ...additionalInputsPayload,
      [`${key}`]: value,
    });
  };

  //Validations messages to be displayed on the modal
  const validationMessages = {
    fileLimit: "Only one file can be uploaded at a time.",
    fileSize: "File size should not exceed 10MB.",
    fileFormat: " Only Excel files (.xlsx, .xls) are allowed.",
  };

  // Allowed file types and max size
  const allowedTypes = [
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
  ];
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes

  // Handle file selection
  const handleFileChange: UploaderProps["onChange"] = (files) => {
    if (files.length > 0) {
      const selectedFile = files[0].blobFile as File;

      if (!allowedTypes.includes(selectedFile.type)) {
        setFilesList([]);
        setAdditionalInputsPayload({
          ...additionalInputsPayload,
          file: undefined,
        });
        return;
      }

      if (selectedFile.size > maxSize) {
        setFilesList([]);
        setAdditionalInputsPayload({
          ...additionalInputsPayload,
          file: undefined,
        });
        return;
      }

      setFilesList(files);
      setAdditionalInputsPayload({
        ...additionalInputsPayload,
        file: selectedFile,
      });
    } else {
      setFilesList([]);
      setAdditionalInputsPayload({
        ...additionalInputsPayload,
        file: undefined,
      });
    }
  };

  const handleFileCheck: UploaderProps["shouldQueueUpdate"] = (
    files,
    newFile,
  ) => {
    // Normalize "files" so that we always work with an array.
    const fileArray: FileType[] = Array.isArray(files) ? files : [files];

    // If more than one file is present, stop the update.
    if (fileArray.length > 1) {
      toaster.push(
        <Message type="error">
          Only one file can be uploaded at a time.
        </Message>,
        { placement: "topCenter" },
      );
      setValidationMessage(validationMessages.fileLimit);
      return false;
    }

    // At this point, fileArray.length === 1.
    const selectedFile = fileArray[0].blobFile as File;

    // Validate the file type.
    if (!allowedTypes.includes(selectedFile.type)) {
      toaster.push(
        <Message type="error">
          Only Excel files (.xlsx, .xls) are allowed.
        </Message>,
        { placement: "topCenter" },
      );
      setValidationMessage(validationMessages.fileFormat);
      return false;
    }

    // Validate the file size.
    if (selectedFile.size > maxSize) {
      toaster.push(
        <Message type="error">File size should not exceed 10MB.</Message>,
        { placement: "topCenter" },
      );
      setValidationMessage(validationMessages.fileSize);
      return false;
    }

    setValidationMessage("");
    // Update state if everything is valid.
    return true;
  };

  const handleClose = () => {
    setOPenModal(false);
    setUploadStatus({
      uploadProjectedRelease: false,
      uploadAgentPricing: false,
    });
    setFilesList([]);
    setAdditionalInputsUploading(false);
    setAdditionalInputsPayload({
      file: undefined,
      date: undefined,
      commodity: commodity ?? undefined,
      email: localStorage.getItem("userEmail"),
      uploadType: undefined,
    });
    setValidationMessage("");
  };

  const HandleSubmit = () => {
    if (
      !additionalInputsPayload.file ||
      !additionalInputsPayload.email ||
      !additionalInputsPayload.commodity ||
      !additionalInputsPayload.uploadType ||
      !additionalInputsPayload
    )
      return;

    if (uploadStatus.uploadProjectedRelease) {
      additionalInputsPayload.projectedReleaseDate = convertDateToMonthYear(
        filters?.date,
      );
    }
    setAdditionalInputsUploading(true);
    mutate(additionalInputsPayload);
  };

  const Header = (
    <span className="text-[0.9vw]">
      {uploadStatus.uploadProjectedRelease
        ? LABELS.uploadProjectedRelease
        : LABELS.uploadAgentPricing}
      <span className="text-[0.9vw] font-semibold pl-[0.75vh] text-deloitteGreen">
        {`for ${filters && Object.keys(filters).length > 0
          ? convertDateToMonthYear(filters?.date)
          : undefined
          }`}
      </span>
    </span>
  );

  function checkDisabled() {
    if (userDetails?.role_name === "Lead") {
      return false;
    }

    if (userDetails?.role_name === "Manager" && commodity) {
      if (!userDetails?.userCommArray.includes(commodity)) {
        return true;
      }
    }

    return false;
  }

  function getLast12MonthsShortAndFullNames() {
    const shortMonthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const fullMonthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const result = [];
    const now = new Date();
    // const after15th = now.getDate() > 15;

    // Determine the starting point
    // - If after the 15th, start 11 months ago and end this month (exclude 12th previous)
    // - If on or before the 15th, go 11 months ago to this month (standard)
    for (let i = 11; i >= 0; i--) {
      // let date;
      // if (after15th) {
      //   // We skip the oldest (i=11), start from 10
      //   if (i === 11) continue;
      //   date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      // } else {
      //   date = new Date(now.getFullYear(), now.getMonth() - i - 1, 1);
      // }
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthIndex = date.getMonth();
      const year = date.getFullYear();
      result.push({
        label: `${shortMonthNames[monthIndex]}-${year}`,
        value: `${fullMonthNames[monthIndex]} ${year}`,
        year,
        month: monthIndex,
      });
    }

    // If after 15th, add next month
    // if (now.getDate() > 15) {
    //   const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    //   const nextMonthIndex = nextMonthDate.getMonth();
    //   const nextMonthYear = nextMonthDate.getFullYear();
    //   result.push({
    //     label: `${shortMonthNames[nextMonthIndex]}-${nextMonthYear}`,
    //     value: `${fullMonthNames[nextMonthIndex]} ${nextMonthYear}`,
    //   });
    // }

    // Ensure only the latest 12 months are included
    return result.reverse().slice(-12);
  }

  useEffect(() => {
    if (isRefetch && !isPending) {
      setCloseValue(null);
      setMonthYear(null);
      setTimeout(() => {
        refetch();
        setIsRefetch(false);
      }, 4000);
    }
  }, [isRefetch, isPending]);

  const formatDateToMonYYYY = (dateString: Date) => {
    if (!dateString) return ""; // Defensive for empty/undefined
    const date = new Date(dateString);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    return `${month}-${year}`;
  };

  const formatDateToLongDate = (input: string): string => {
    const date = new Date(input); // Parse to Date object
    const day = date.getUTCDate().toString().padStart(2, "0");
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();

    return `${day} ${month} ${year}`;
  };

  return (
    <>
      <div className="bg-[#F1F1F1] grow shrink p-[2%] pr-[5%] flex flex-col flex-nowrap justify-start items-start gap-[1vw] h-full w-full">
        <div className="relative w-full">
        {isPending && (
          <Loader
            backdrop
            content="Loading..."
            size="md"
            vertical
            className="text-black font-bold"
            style={{ opacity: 0.6, zIndex: 10 }}
          />
        )}
        <Accordion
          className="w-full bg-white border border-[#bebebe] p-0"
          activeKey={activeKeyValue}
          onSelect={(eventKey) =>
            eventKey &&
            eventKey !== "-" &&
            typeof eventKey === "string" &&
            handleToggle(eventKey)
          }
        >
          <Accordion.Panel
            key={1}
            header={
              <div className="font-bold text-fs16 rounded-md flex justify-between items-center p-0 h-[6vh]">
                <div className="flex justify-start items-center gap-2">
                  <div>
                    {activeKeyValue === "index" ? <MinusIcon /> : <PlusIcon />}
                  </div>
                  <div className="text-[1.8vh]">
                    {LABELS.editCommodityIndex}
                    {commodity === "Copper Wire" && (
                      <span className="text-[1.8vh]"> for the week of {getMondayOfCurrentWeekLabel()}</span>
                    )}
                    {indexTableData?.index_uploaded_last && (
                      <span className="text-[1.5vh] font-semibold italic">
                        {` (last updated on ${moment
                          .utc(indexTableData?.index_uploaded_last)
                          .format("DD-MMM-YYYY")} ${indexTableData?.index_uploaded_by &&
                          ` by ${indexTableData?.index_uploaded_by}`
                          })`}
                      </span>
                    )}
                  </div>
                </div>
                {userDetails.userCanEditArray.includes("Quote Upload") && (
                  <div
                    className="flex justify-end items-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {commodity !== "Copper Wire" && (
                    <div className="flex flex-col justify-end items-start gap-2">
                      <label className="font-bold text-[0.8vw]">Date</label>
                      <SelectPicker
                        menuAutoWidth
                        className="dropdown-responsive-width"
                        labelKey="label"
                        valueKey="value"
                        value={monthYear}
                        placeholder="Select Date"
                        cleanable={false}
                        style={{ width: width }}
                        onChange={(value: string | null) => {
                          setMonthYear(value);
                        }}
                        data={
                          filters?.date
                            ? [
                                {
                                  label: getCurrentMonthYear(0),
                                  value: getCurrentMonthYear(0),
                                },
                                {
                                  label: getCurrentMonthYear(1),
                                  value: getCurrentMonthYear(1),
                                },
                              ]
                            : []
                        }
                      />
                    </div>
                    )}
                    <div className="flex flex-col justify-end items-start gap-2">
                      <label className="font-bold text-[0.8vw]">Index</label>
                      <Input
                        className="font-normal text-[0.8vw] text-black placeholder-black"
                        type="number"
                        size="md"
                        placeholder="Enter a number or decimal"
                        style={{ width: width }}
                        value={closevalue !== null ? closevalue.toString() : ""}
                        onChange={(value: string) => {
                          if (
                            value === "" ||
                            (!isNaN(Number(value)) && Number(value) >= 0)
                          ) {
                            setCloseValue(value === "" ? null : Number(value));
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "-") {
                            e.preventDefault();
                          }
                        }}
                        min={0}
                      />
                    </div>

                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        let calculatedMonth = monthYear;
                        if (commodity === "Copper Wire" && filters?.date_wire) {
                          const wireDate = new Date(filters.date_wire);
                          wireDate.setDate(wireDate.getDate() - 7);
                          const day = wireDate.getUTCDate().toString().padStart(2, "0");
                          const monthNames = [
                            "January", "February", "March", "April", "May", "June",
                            "July", "August", "September", "October", "November", "December",
                          ];
                          const monthName = monthNames[wireDate.getUTCMonth()];
                          const year = wireDate.getUTCFullYear();
                          calculatedMonth = `${day} ${monthName} ${year}`;
                        }
                        let payload = {
                          commodity: commodity,
                          close: closevalue,
                          month: calculatedMonth,
                          email: localStorage.getItem("userEmail"),
                        };
                        indexMutate(payload);
                      }}
                      disabled={
                        closevalue === null ||
                        (commodity !== "Copper Wire" && monthYear === null)
                      }
                      className={`text-[1.8vh] w-[7vw] font-semibold bg-deloitteGreen rounded text-white px-[1.2vw] py-[1vh] mr-[1.5vw] ${closevalue === null || (commodity !== "Copper Wire" && monthYear === null)
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                        }`}
                    >
                      {LABELS.save}
                    </button>
                  </div>
                )}
              </div>
            }
            eventKey={"index"}
            caretAs={() => null}
          >
            {activeKeyValue !== "" &&
              (isRefetch || isIndexStatusLoading || isPending ? (
                // <div className="absolute w-full h-[35vh] overflow-y-auto custom-scrollbar">
                <Loader
                  backdrop
                  content="Loading..."
                  size="md"
                  vertical
                  className="text-black font-bold"
                  style={{ opacity: 0.6 }}
                />
              ) : (
                // </div>
                <div className="w-full h-[35vh] overflow-y-auto custom-scrollbar">
                  <Table
                    // className="h-full"
                    data={indexTableData?.indexValues ?? []}
                    fillHeight
                    bordered
                    cellBordered
                  // headerHeight={80}
                  >
                    <RSuiteColumn
                      flexGrow={1}
                      minWidth={120}
                      fixed
                      verticalAlign="middle"
                    >
                      <HeaderCell>Date</HeaderCell>
                      <Cell dataKey="month">
                        {(value) =>
                          commodity === "Copper Wire"
                            ? formatDateToLongDate(value.month)
                            : formatDateToMonYYYY(value.month)
                        }
                      </Cell>
                    </RSuiteColumn>
                    <RSuiteColumn
                      flexGrow={1}
                      minWidth={120}
                      fixed
                      verticalAlign="middle"
                    >
                      <HeaderCell>Index</HeaderCell>
                      <Cell dataKey="close" />
                    </RSuiteColumn>
                  </Table>
                </div>
              ))}
          </Accordion.Panel>
          <Accordion.Panel
            key={2}
            header={
              <div className="font-bold text-fs16 rounded-md flex justify-between items-center p-0 h-[6vh]">
                <div className="flex justify-between items-center gap-2 w-full">
                  <div className="flex justify-start items-center gap-2">
                  <div>
                    {activeKeyValue === "projectedRelease" ? <MinusIcon /> : <PlusIcon />}
                  </div>
                  <div className="text-[1.8vh]">
                    {LABELS.uploadProjectedRelease}
                    <span className="text-[1.8vh] font-semibold text-black">
                      {` for ${
                        filters && Object.keys(filters).length > 0
                          ? convertDateToMonthYear(filters?.date)
                          : ""
                      }`}
                    </span>
                    {indexTableData?.projected_release_uploaded_last && (
                      <span className="text-[1.5vh] font-semibold italic">
                        {` (last uploaded on ${moment
                          .utc(indexTableData?.projected_release_uploaded_last)
                          .format("DD-MMM-YYYY")} ${indexTableData?.projected_release_uploaded_by &&
                          ` by ${indexTableData?.projected_release_uploaded_by}`
                          })`}{" "}
                      </span>
                    )}
                  </div>
                  </div>
                  <div
                    className="flex"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!checkDisabled() && (
                      <div>
                        <a
                          download={"Projected Release"}
                          style={{ textDecoration: "none" }}
                          href={
                            commodity === "Steel Conduit"
                              ? require("../../../assets/Projected Releases Steel.xlsx")
                              : commodity === "PVC Conduit"
                              ? require("../../../assets/Projected Releases PVC.xlsx")
                              : require("../../../assets/Projected Releases Copper.xlsx")
                          }
                        >
                          <div className="cursor-not-allowed flex justify-center items-center bg-deloitteGreen rounded px-[1.2vw] py-[1vh] mr-[1.5vw]">
                            <img
                              className="h-[2vh]"
                              src={UserGuideDownloadIcon}
                              alt="Download User Guide"
                            />
                            <button
                              onClick={() => { }}
                              disabled={uploadStatus.uploadAgentPricing}
                              className={`text-[1.8vh] font-semibold text-white ml-[0.5vw]  `}
                            >
                              {LABELS.downloadTemp}
                            </button>
                          </div>
                        </a>
                      </div>
                    )}
                    <div>
                      {userDetails.userCanEditArray.includes(
                        "Quote Upload",
                      ) && (
                          <button
                            // disabled={!allowAssessment}
                            onClick={() => {
                              handleClickUpload(
                                { uploadProjectedRelease: true },
                                "uploadProjectedRelease",
                              );
                            }}
                            disabled={checkDisabled()}
                            className={`text-[1.8vh] w-[7vw] font-semibold bg-deloitteGreen rounded text-white px-[1.2vw] py-[1vh] mr-[1.5vw] ${checkDisabled() //allowAssessment
                              ? "cursor-not-allowed opacity-50"
                              : "cursor-pointer"
                              }`}
                          >
                            {LABELS.upload}
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            }
            eventKey={"projectedRelease"}
            caretAs={() => null}
          >
            {activeKeyValue === "projectedRelease" && (
              <div className="w-full h-[35vh] overflow-y-auto custom-scrollbar">
                <Table
                  data={(indexTableData?.projected_releases as DistrictLbsRow[]) ?? []}
                  fillHeight
                  bordered
                  cellBordered
                >
                  <RSuiteColumn
                    flexGrow={1}
                    minWidth={120}
                    fixed
                    verticalAlign="middle"
                  >
                    <HeaderCell>District</HeaderCell>
                    <Cell dataKey="district" />
                  </RSuiteColumn>
                  <RSuiteColumn
                    flexGrow={1}
                    minWidth={120}
                    fixed
                    verticalAlign="middle"
                  >
                    <HeaderCell>Lbs</HeaderCell>
                    <Cell dataKey="lbs">
                      {(rowData: DistrictLbsRow) =>
                        formatNumberwithCommas(rowData.lbs)
                      }
                    </Cell>
                  </RSuiteColumn>
                </Table>
              </div>
            )}
          </Accordion.Panel>
          <Accordion.Panel
            key={3}
            header={
              <div className="font-bold text-fs16 rounded-md flex justify-between items-center p-0 h-[6vh]">
                <div className="flex justify-between items-center gap-2 w-full">
                  <div className="flex justify-start items-center gap-2">
                  <div>
                    {activeKeyValue === "marketPricing" ? <MinusIcon /> : <PlusIcon />}
                  </div>
                  <div className="text-[1.8vh]">
                    {LABELS.uploadAgentPricing}
                    <span className="text-[1.8vh] font-semibold text-black">
                      {` for ${
                        filters && Object.keys(filters).length > 0
                          ? convertDateToMonthYear(filters?.date)
                          : ""
                      }`}
                    </span>
                    {indexTableData?.agent_pricing_uploaded_till && (
                      <span className="text-[1.5vh] font-semibold italic">
                        {` (last uploaded on ${moment
                          .utc(indexTableData?.agent_pricing_uploaded_till)
                          .format("DD-MMM-YYYY")} ${indexTableData?.agent_pricing_uploaded_by &&
                          ` by ${indexTableData?.agent_pricing_uploaded_by}`
                          })`}
                      </span>
                    )}
                  </div>
                  </div>
                  <div
                    className="flex"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!checkDisabled() && (
                      <div>
                        <a
                          download={"Agent Quotes"}
                          style={{ textDecoration: "none" }}
                          href={
                            commodity === "Steel Conduit"
                              ? require("../../../assets/Agent Quotes Steel.xlsx")
                              : commodity === "PVC Conduit"
                              ? require("../../../assets/Agent Quotes PVC.xlsx")
                              : require("../../../assets/agent_pricing_template_wire.xlsx")
                          }
                        >
                          <div className=" flex justify-center items-center bg-deloitteGreen rounded px-[1.2vw] py-[1vh] mr-[1.5vw] cursor-pointer">
                            <img
                              className="h-[2vh]"
                              src={UserGuideDownloadIcon}
                              alt="Download User Guide"
                            />
                            <button
                              onClick={() => { }}
                              disabled={uploadStatus.uploadAgentPricing}
                              className={`text-[1.8vh] font-semibold text-white ml-[0.5vw]  `}
                            >
                              {LABELS.downloadTemp}
                            </button>
                          </div>
                        </a>
                      </div>
                    )}
                    <div>
                      {userDetails.userCanEditArray.includes(
                        "Quote Upload",
                      ) && (
                          <button
                            // disabled={!allowAssessment}
                            onClick={() => {
                              handleClickUpload(
                                { uploadAgentPricing: true },
                                "uploadAgentPricing",
                              );
                            }}
                            disabled={checkDisabled()}
                            className={`text-[1.8vh] w-[7vw] font-semibold bg-deloitteGreen rounded text-white px-[1.2vw] py-[1vh] mr-[1.5vw] ${checkDisabled() //allowAssessment
                              ? "cursor-not-allowed opacity-50"
                              : "cursor-pointer"
                              }`}
                          >
                            {LABELS.upload}
                          </button>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            }
            eventKey={"marketPricing"}
            caretAs={() => null}
          >
            {activeKeyValue === "marketPricing" && (
              <div className="w-full h-[35vh] overflow-y-auto custom-scrollbar">
                <Table
                  data={(indexTableData?.agent_pricing as DistrictQuotesRow[]) ?? []}
                  fillHeight
                  bordered
                  cellBordered
                >
                  <RSuiteColumn
                    flexGrow={1}
                    minWidth={120}
                    fixed
                    verticalAlign="middle"
                  >
                    <HeaderCell>District</HeaderCell>
                    <Cell dataKey="district" />
                  </RSuiteColumn>
                  <RSuiteColumn
                    flexGrow={1}
                    minWidth={120}
                    fixed
                    verticalAlign="middle"
                  >
                    <HeaderCell>Base SKU</HeaderCell>
                    <Cell dataKey="base_sku" />
                  </RSuiteColumn>
                  <RSuiteColumn
                    flexGrow={1}
                    minWidth={120}
                    fixed
                    verticalAlign="middle"
                  >
                    <HeaderCell>Quotes</HeaderCell>
                    <Cell dataKey="quotes">
                      {(rowData: DistrictQuotesRow) =>
                        `$${formatNumberwithCommas(rowData.quotes, true)}`
                      }
                    </Cell>
                  </RSuiteColumn>
                </Table>
              </div>
            )}
          </Accordion.Panel>
        </Accordion>
        </div>

        {/* <span className="w-full bg-white font-bold p-pd13 shadow-module-card border border-[#bebebe] text-fs16 rounded-md flex justify-between items-center">
          <span className="text-[1.8vh]">
            {LABELS.uploadProjectedRelease}
            {indexTableData?.projected_release_uploaded_last && (
              <span className="text-[1.5vh] font-semibold italic">
                {` (last uploaded on ${moment
                  .utc(indexTableData?.projected_release_uploaded_last)
                  .format("DD-MMM-YYYY")} ${
                  indexTableData?.projected_release_uploaded_by &&
                  ` by ${indexTableData?.projected_release_uploaded_by}`
                })`}{" "}
              </span>
            )}
          </span>
          <div className="flex">
            {!checkDisabled() && (
              <div>
                <a
                  download={"Projected Release"}
                  style={{ textDecoration: "none" }}
                  href={require("../../../assets/projected-releases.xlsx")}
                >
                  <div className="cursor-not-allowed flex justify-center items-center bg-deloitteGreen rounded px-[1.2vw] py-[1vh] mr-[1.5vw]">
                    <img
                      className="h-[2vh]"
                      src={UserGuideDownloadIcon}
                      alt="Download User Guide"
                    />
                    <button
                      onClick={() => {}}
                      disabled={uploadStatus.uploadAgentPricing}
                      className={`text-[1.8vh] font-semibold text-white ml-[0.5vw]  `}
                    >
                      {LABELS.downloadTemp}
                    </button>
                  </div>
                </a>
              </div>
            )}
            <div>
              {userDetails.userCanEditArray.includes("Quote Upload") && (
                <button
                  // disabled={!allowAssessment}
                  onClick={() => {
                    handleClickUpload(
                      { uploadProjectedRelease: true },
                      "uploadProjectedRelease"
                    );
                  }}
                  disabled={checkDisabled()}
                  className={`text-[1.8vh] w-[7vw] font-semibold bg-deloitteGreen rounded text-white px-[1.2vw] py-[1vh] mr-[1.5vw] ${
                    checkDisabled() //allowAssessment
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  }`}
                >
                  {LABELS.upload}
                </button>
              )}
            </div>
          </div>
        </span> */}
        {/* <span className="w-full bg-white font-bold p-pd13 shadow-module-card border border-[#bebebe] text-fs16 rounded-md flex justify-between items-center">
          <span className="text-[1.8vh]">
            {LABELS.uploadAgentPricing}
            {indexTableData?.agent_pricing_uploaded_till && (
              <span className="text-[1.5vh] font-semibold italic">
                {` (last uploaded on ${moment
                  .utc(indexTableData?.agent_pricing_uploaded_till)
                  .format("DD-MMM-YYYY")} ${
                  indexTableData?.agent_pricing_uploaded_by &&
                  ` by ${indexTableData?.agent_pricing_uploaded_by}`
                })`}
              </span>
            )}
          </span>
          <div className="flex">
            {!checkDisabled() && (
              <div>
                <a
                  download={"Agent Quotes"}
                  style={{ textDecoration: "none" }}
                  href={require("../../../assets/agent-quotes.xlsx")}
                >
                  <div className=" flex justify-center items-center bg-deloitteGreen rounded px-[1.2vw] py-[1vh] mr-[1.5vw] cursor-pointer">
                    <img
                      className="h-[2vh]"
                      src={UserGuideDownloadIcon}
                      alt="Download User Guide"
                    />
                    <button
                      onClick={() => {}}
                      disabled={uploadStatus.uploadAgentPricing}
                      className={`text-[1.8vh] font-semibold text-white ml-[0.5vw]  `}
                    >
                      {LABELS.downloadTemp}
                    </button>
                  </div>
                </a>
              </div>
            )}
            <div>
              {userDetails.userCanEditArray.includes("Quote Upload") && (
                <button
                  // disabled={!allowAssessment}
                  onClick={() => {
                    handleClickUpload(
                      { uploadAgentPricing: true },
                      "uploadAgentPricing"
                    );
                  }}
                  disabled={checkDisabled()}
                  className={`text-[1.8vh] w-[7vw] font-semibold bg-deloitteGreen rounded text-white px-[1.2vw] py-[1vh] mr-[1.5vw] ${
                    checkDisabled() //allowAssessment
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  }`}
                >
                  {LABELS.upload}
                </button>
              )}
            </div>
          </div>
        </span> */}
      </div>
      {openModal && (
        <CustomModal
          openModal={openModal}
          closeModal={handleClose}
          isloading={false}
          header={Header}
          footer="Upload an excel (.xlsx, .xls) with size less than 10MB"
        >
          <div>
            <div className="px-[1vh]">
              <Uploader
                autoUpload={false}
                fileList={filesList}
                action="#"
                draggable
                shouldQueueUpdate={handleFileCheck}
                onChange={handleFileChange}
                accept=".xlsx,.xls"
                multiple={false}
              >
                <div
                  style={{
                    height: "12vw",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FileUploadIcon
                    style={{ fontSize: "5vw", color: "#86BC25" }}
                  />
                  <span className="text-fs16 mt-[2vh]">
                    Click or Drag files to this area to upload
                  </span>
                </div>
              </Uploader>
            </div>
            {validationMessage && (
              <div className="text-[0.75vw] font-semibold text-red-700 w-full flex justify-start px-[1vh]">
                *{validationMessage}
              </div>
            )}
            <div className="flex justify-center pt-[1vh]">
              <Button
                className="w-[5vw]"
                label={"Submit"}
                bgColor={"#86BC25"}
                onClick={HandleSubmit}
                disabled={
                  !additionalInputsPayload.file ||
                  !additionalInputsPayload.email ||
                  !additionalInputsPayload.commodity ||
                  !additionalInputsPayload.uploadType
                }
              />
            </div>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default AdditionalInputs;
